import { useEffect } from "react";
import { useAppDispatch } from "@/store/hooks";
import { User } from "oidc-client-ts";
import { loginSuccess, logout, loginFailed } from "@/store/authSlice";
import { setUserInfo, clearUser } from "@/store/userSlice";
import { AuthService } from "@/service/AuthService";
import { TokenService } from "@/service/TokenService";
import Logger from "@/utils/Logger";
import { AppSettingsService } from "@/service/AppSettingsService";

interface UseAuthStateProps {
  oidcUser: User | null;
  isLoading: boolean;
  error: Error | null;
}

export const useAuthState = ({
  oidcUser,
  isLoading,
  error,
}: UseAuthStateProps) => {
  const dispatch = useAppDispatch();

  // Sync OIDC state with Redux store
  useEffect(() => {
    if (oidcUser && !oidcUser.expired) {
      const userProfile = AuthService.extractUserProfile(oidcUser);
      const userInfo = AuthService.extractUserInfo(oidcUser);

      // Set auth state (without roles)
      dispatch(
        loginSuccess({
          token: oidcUser.access_token,
          user: userProfile,
          expiresAt: TokenService.getTokenExpirationTime(oidcUser),
        }),
      );

      // Set user info (with roles)
      dispatch(setUserInfo(userInfo));

      Logger.info("User authenticated - Redux state updated", [
        "useAuthState",
        JSON.stringify({
          user: userProfile,
          roles: userInfo.appRoles,
          userInfo: userInfo,
        }),
      ]);
    } else if (
      !AppSettingsService.isOAuthLocalHostBypassEnabled() &&
      window.location.hostname === "localhost"
    ) {
      // Development mode fallback
      const devUser = AuthService.getDevelopmentUser();
      const devUserInfo = AuthService.getDevelopmentUserInfo();

      dispatch(
        loginSuccess({
          token: TokenService.getDevelopmentToken() || "",
          user: devUser,
          expiresAt: Math.floor(Date.now() / 1000) + 3600,
        }),
      );

      dispatch(setUserInfo(devUserInfo));

      Logger.info("Development mode - mock user authenticated", [
        "useAuthState",
        JSON.stringify({
          user: devUser,
          roles: devUserInfo.appRoles,
          userInfo: devUserInfo,
        }),
      ]);
    } else if (!isLoading) {
      dispatch(logout());
      dispatch(clearUser());
      // Logger.info("User not authenticated - Redux state cleared", ["useAuthState"]);
    }
  }, [oidcUser, isLoading, dispatch]);

  // Handle OIDC errors
  useEffect(() => {
    if (error) {
      Logger.error("OIDC authentication error", error, ["useAuthState"]);
      dispatch(loginFailed(error.message || "Authentication failed"));
      dispatch(clearUser());
    }
  }, [error, dispatch]);
};
